import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type { IAiScriptStatementResponse } from "@/payroll/ai-script/types";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import { aiScriptStatementKey } from "./keys";
import {
  getAiScriptStatementPath,
  getAiScriptTechnicianStatementPath,
} from "./paths";

export const STATEMENT_IDENTITY = {
  viewer: "viewer",
  technician: "technician",
} as const;

export type StatementIdentity =
  (typeof STATEMENT_IDENTITY)[keyof typeof STATEMENT_IDENTITY];

type StatementIdentityParams =
  | { identity: typeof STATEMENT_IDENTITY.viewer; technicianId?: never }
  | {
      identity: typeof STATEMENT_IDENTITY.technician;
      technicianId: string | null;
    };

export const useGetAiScriptStatement = (
  businessId: string | null,
  runId: string | null,
  {
    identity,
    technicianId,
    enabled,
  }: StatementIdentityParams & { enabled: boolean },
) => {
  const api = useApi();
  const named = identity === STATEMENT_IDENTITY.technician;
  const requestedId = named ? (technicianId ?? null) : null;

  const query = useQuery({
    queryKey: aiScriptStatementKey(businessId, runId, requestedId),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(
          requestedId
            ? getAiScriptTechnicianStatementPath(businessId, runId, requestedId)
            : getAiScriptStatementPath(businessId, runId),
        )
        .then((res) => res.data as IAiScriptStatementResponse);
    },
    enabled: enabled && !!businessId && !!runId && (!named || !!requestedId),
    retry: false,
    ...(named
      ? {
          staleTime: 0,
          refetchOnMount: "always" as const,
          refetchInterval: 30_000,
        }
      : {
          staleTime: 0,
        }),
  });
  return named ? currentPayrollResult(query) : query;
};
